// src/pages/Transportadora.jsx
import React, { useCallback, useEffect, useState } from "react";
import styles from "../style/Transportadora.module.css";
import TablaTransportadora from "../components/TablaTransportadora";
import MapaTransportadora from "../components/MapaTransportadora";
import TablaColecta from "../components/TablaColecta";
import MapaColecta from "../components/MapaColecta";

// Updaters (según la vista)
const UPDATER_NOVEDAD_URL = "https://marketbusters.pro/Dropdrop/dropdrop_listado_novedad_encamino.php";
const UPDATER_COLECTA_URL = "https://marketbusters.pro/Dropdrop/dropdrop_listado_preparando_guia.php";

export default function Transportadora() {
  // tabs: "tabla" | "mapa" | "colecta" | "mapa_colecta"
  const [tab, setTab] = useState("tabla");

  // keys para forzar recarga en hijos
  const [refreshKeyGeneral, setRefreshKeyGeneral] = useState(0);   // tabla / mapa (novedad+camino)
  const [refreshKeyColecta, setRefreshKeyColecta] = useState(0);   // tabla / mapa (preparando+guía)

  const [updating, setUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updateError, setUpdateError] = useState("");

  const runUpdater = useCallback(async (url) => {
    const res = await fetch(url, { cache: "no-store", mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  }, []);

  // Dispara el updater correspondiente al tab actual
  const runUpdaterForTab = useCallback(async (targetTab) => {
    setUpdating(true);
    setUpdateError("");
    try {
      if (targetTab === "colecta" || targetTab === "mapa_colecta") {
        await runUpdater(UPDATER_COLECTA_URL);
        setRefreshKeyColecta(Date.now());
      } else {
        await runUpdater(UPDATER_NOVEDAD_URL);
        setRefreshKeyGeneral(Date.now());
      }
      setLastUpdated(new Date());
    } catch (e) {
      setUpdateError(`No se pudo actualizar datos: ${e.message}`);
    } finally {
      setUpdating(false);
    }
  }, [runUpdater]);

  // 🚀 Al montar: disparar AMBOS updaters en paralelo para tener todo listo
  useEffect(() => {
    (async () => {
      setUpdating(true);
      setUpdateError("");
      try {
        await Promise.all([runUpdater(UPDATER_NOVEDAD_URL), runUpdater(UPDATER_COLECTA_URL)]);
        setRefreshKeyGeneral(Date.now());
        setRefreshKeyColecta(Date.now());
        setLastUpdated(new Date());
      } catch (e) {
        setUpdateError(`No se pudo actualizar datos iniciales: ${e.message}`);
      } finally {
        setUpdating(false);
      }
    })();
  }, [runUpdater]);

  // Al cambiar de tab: actualizar lo que corresponda
  useEffect(() => {
    runUpdaterForTab(tab);
  }, [tab, runUpdaterForTab]);

  const onManualUpdate = () => runUpdaterForTab(tab);

  return (
    <div className={styles.transportadoraRoot}>
      {/* Tabs + acción */}
      <div className={styles.tabBar} style={{ gap: 8, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", alignItems: "center" }}>
          <button
            className={`${styles.tabBtn} ${tab === "tabla" ? styles.tabBtnActive : ""}`}
            onClick={() => setTab("tabla")}
          >
            TABLA
          </button>
          <button
            className={`${styles.tabBtn} ${tab === "mapa" ? styles.tabBtnActive : ""}`}
            onClick={() => setTab("mapa")}
          >
            MAPA
          </button>
          <button
            className={`${styles.tabBtn} ${tab === "colecta" ? styles.tabBtnActive : ""}`}
            onClick={() => setTab("colecta")}
          >
            TABLA COLECTA
          </button>
          <button
            className={`${styles.tabBtn} ${tab === "mapa_colecta" ? styles.tabBtnActive : ""}`}
            onClick={() => setTab("mapa_colecta")}
          >
            MAPA COLECTA
          </button>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "nowrap" }}>
          <button
            onClick={onManualUpdate}
            disabled={updating}
            className={styles.tabBtn}
            style={{ background: updating ? "#374151" : "#111827", border: "1px solid #272b38", padding: "8px 12px" }}
            title="Forzar actualización"
          >
            {updating ? "Actualizando…" : "Actualizar ahora"}
          </button>
          {lastUpdated && (
            <span style={{ fontSize: 12, opacity: 0.8, whiteSpace: "nowrap" }}>
              Última act.: {lastUpdated.toLocaleTimeString("es-CL")}
            </span>
          )}
        </div>
      </div>

      {updateError && (
        <div className={styles.card} style={{ padding: 12, border: "1px solid #b91c1c", color: "#fecaca" }}>
          {updateError}
        </div>
      )}

      {/* Contenido según tab */}
      {tab === "tabla" && <TablaTransportadora refreshKey={refreshKeyGeneral} />}
      {tab === "mapa" && <MapaTransportadora refreshKey={refreshKeyGeneral} />}
      {tab === "colecta" && <TablaColecta refreshKey={refreshKeyColecta} />}
      {tab === "mapa_colecta" && <MapaColecta refreshKey={refreshKeyColecta} />}
    </div>
  );
}
